import { useQueryClient } from "@tanstack/react-query";
import { VscodeTextarea } from "@vscode-elements/react-elements";
import clsx from "clsx";
import {
  FC,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Result, SearchInput } from "@tsmono/inspect-common/types";
import {
  AutocompleteInput,
  LoadingBar,
  MarkdownDivWithReferences,
  MarkdownReference,
  PopOver,
  SegmentedControl,
  SidebarHeader,
} from "@tsmono/react/components";
import { useInterval } from "@tsmono/react/hooks";
import { Icon, IconName } from "@tsmono/react/icons";
import {
  ApiError,
  autosizeTextarea,
  AutosizeTextareaConfig,
} from "@tsmono/util";

import styles from "./SearchPanel.module.css";
import {
  applySearchTypeChange,
  createInitialSearchPanelState,
  SearchPanelState,
} from "./searchPanelState";
import { buildSearchRequest, buildSearchScope } from "./searchRequest";
import type { GrepOptions } from "./searchRequest";
import type {
  ModelHistoryController,
  SearchPanelApi,
  SearchPanelNavigation,
  SearchPanelStateController,
  SearchScope,
  SearchType,
} from "./types";
import {
  searchQueryKeys,
  useCachedSearchResult,
  useCreateSearch,
  useSearches,
} from "./useSearchQueries";

export type SearchPanelProps = {
  scope: SearchScope;
  api: SearchPanelApi;
  stateController: SearchPanelStateController;
  navigation: SearchPanelNavigation;
  defaultModel?: string;
  modelHistory?: ModelHistoryController;
  onClose: () => void;
};

function hasStringValue(target: EventTarget | null): target is EventTarget & {
  value: string;
} {
  return (
    target !== null && "value" in target && typeof target.value === "string"
  );
}

function getInputValue(e: Event): string {
  return hasStringValue(e.target) ? e.target.value : "";
}

const SEARCH_TEXTAREA_AUTOSIZE: AutosizeTextareaConfig = {
  minRows: 2,
  maxRows: 10,
};

function getInnerTextarea(el: HTMLElement | null): HTMLTextAreaElement | null {
  const inner = el?.shadowRoot?.querySelector("textarea");
  return inner instanceof HTMLTextAreaElement ? inner : null;
}

function isSearchType(value: string): value is SearchType {
  return value === "llm" || value === "grep";
}

function applyRecentSearch(
  prev: SearchPanelState,
  search: SearchInput
): SearchPanelState {
  if (search.type === "llm") {
    return {
      ...prev,
      searchType: "llm",
      searches: {
        ...prev.searches,
        llm: {
          ...prev.searches.llm,
          query: search.query,
          model: search.model ?? "",
          searchId: search.search_id,
        },
      },
    };
  }

  return {
    ...prev,
    searchType: "grep",
    searches: {
      ...prev.searches,
      grep: {
        ...prev.searches.grep,
        query: search.query,
        grepOptions: {
          ignoreCase: search.ignore_case,
          regex: search.regex,
          wordBoundary: search.word_boundary,
        },
        searchId: search.search_id,
      },
    },
  };
}

const NOOP_MODEL_HISTORY: ModelHistoryController = {
  history: [],
  record: () => {},
};

export const SearchPanel: FC<SearchPanelProps> = ({
  scope,
  api,
  stateController,
  navigation,
  defaultModel,
  modelHistory = NOOP_MODEL_HISTORY,
  onClose,
}) => {
  const { getMessageUrl, getEventUrl, getEventMessageUrl } = navigation;
  const modelInputId = useId();
  const queryClient = useQueryClient();

  // The host normalizes at its store boundary, so `state` is already a full
  // SearchPanelState and `setState` can take updaters untouched.
  const { state, setState } = stateController;
  const { searchType } = state;
  const activeBranch = state.searches[searchType];
  const { query, searchId } = activeBranch;
  const grepOptions = state.searches.grep.grepOptions;
  const model = state.searches.llm.model;

  const createSearchMutation = useCreateSearch({ api });
  const cachedSearchQuery = useCachedSearchResult({
    api,
    scope: buildSearchScope(scope),
    searchId,
  });

  const [isRecentOpen, setIsRecentOpen] = useState(false);
  const recentButtonRef = useRef<HTMLButtonElement>(null);

  const loading =
    createSearchMutation.isPending || cachedSearchQuery.isFetching;
  const error = createSearchMutation.error || cachedSearchQuery.error;
  const currentSearch: Result | null = cachedSearchQuery.data ?? null;
  const hasSearched =
    !!searchId ||
    createSearchMutation.isPending ||
    !!createSearchMutation.error;

  const clearSearchIdForCurrentType = useCallback(() => {
    createSearchMutation.reset();
    setState((prev) => ({
      ...prev,
      searches: {
        ...prev.searches,
        [prev.searchType]: {
          ...prev.searches[prev.searchType],
          searchId: null,
        },
      },
    }));
  }, [createSearchMutation, setState]);

  const cacheKey = api.cacheKey;

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const text = query.trim();
      if (!text || loading) return;

      clearSearchIdForCurrentType();

      const resolvedModel =
        searchType === "llm"
          ? ((model.trim() || defaultModel?.trim()) ?? "")
          : "";

      const request = buildSearchRequest({
        grepOptions,
        model: resolvedModel,
        query: text,
        scope,
        searchType,
      });

      createSearchMutation.mutate(request, {
        onSuccess: (response) => {
          queryClient.setQueryData(
            searchQueryKeys.cachedResult({
              cacheKey,
              scope: buildSearchScope(scope),
              searchId: response.id,
            }),
            response.result
          );
          setState((prev) => ({
            ...prev,
            searches: {
              ...prev.searches,
              [searchType]: {
                ...prev.searches[searchType],
                searchId: response.id,
              },
            },
          }));
          if (searchType === "llm" && resolvedModel) {
            modelHistory.record(resolvedModel);
          }
        },
      });
    },
    [
      cacheKey,
      clearSearchIdForCurrentType,
      createSearchMutation,
      defaultModel,
      grepOptions,
      loading,
      model,
      modelHistory,
      query,
      queryClient,
      scope,
      searchType,
      setState,
    ]
  );

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.closest("form")?.requestSubmit();
    }
  }, []);

  const handleSelectRecent = useCallback(
    (search: SearchInput) => {
      createSearchMutation.reset();
      setIsRecentOpen(false);
      setState((prev) => applyRecentSearch(prev, search));
    },
    [createSearchMutation, setState]
  );

  const handleNewSearch = useCallback(() => {
    createSearchMutation.reset();
    setState(() => createInitialSearchPanelState());
  }, [createSearchMutation, setState]);

  const toggleGrepOption = useCallback(
    (key: keyof GrepOptions) => {
      createSearchMutation.reset();
      setState((prev) => ({
        ...prev,
        searches: {
          ...prev.searches,
          grep: {
            ...prev.searches.grep,
            grepOptions: {
              ...prev.searches.grep.grepOptions,
              [key]: !prev.searches.grep.grepOptions[key],
            },
            searchId: null,
          },
        },
      }));
    },
    [createSearchMutation, setState]
  );

  const handleSearchTypeChange = useCallback(
    (type: SearchType) => {
      setState((prev) => applySearchTypeChange(prev, type));
    },
    [setState]
  );

  const formRef = useRef<HTMLFormElement>(null);
  const handleModelCommit = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  const searchTextareaRef = useRef<HTMLElement | null>(null);
  const handleSearchTextareaRef = useCallback((el: HTMLElement | null) => {
    searchTextareaRef.current = el;
  }, []);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useLayoutEffect(() => {
    const inner = getInnerTextarea(searchTextareaRef.current);
    if (inner !== null) {
      autosizeTextarea(inner, SEARCH_TEXTAREA_AUTOSIZE);
    }
  }, [query]);

  const handleQueryInput = useCallback(
    (e: Event) => {
      const value = getInputValue(e);
      setState((prev) => ({
        ...prev,
        searches: {
          ...prev.searches,
          [prev.searchType]: {
            ...prev.searches[prev.searchType],
            query: value,
          },
        },
      }));
    },
    [setState]
  );

  const handleModelChange = useCallback(
    (value: string) => {
      setState((prev) => ({
        ...prev,
        searches: {
          ...prev.searches,
          llm: {
            ...prev.searches.llm,
            model: value,
          },
        },
      }));
    },
    [setState]
  );

  // Seed the model input with the most recent model on open. Marking the ref
  // done on the first run (not only when it fills) means a later clear — or a
  // persisted model — is never fought.
  const didPrefillModelRef = useRef(false);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (didPrefillModelRef.current) return;
    didPrefillModelRef.current = true;
    const mostRecent = modelHistory.history[0];
    if (model === "" && mostRecent !== undefined) {
      handleModelChange(mostRecent);
    }
  }, [model, modelHistory.history, handleModelChange]);

  return (
    <div className={styles.container}>
      <SidebarHeader
        icon="search"
        title={`Search: ${scope}`}
        onClose={onClose}
      />
      <div className={styles.body}>
        <form
          ref={formRef}
          className={styles.searchArea}
          onSubmit={handleSubmit}
        >
          <div className={styles.topRow}>
            <div className={styles.typeToggle}>
              <SegmentedControl
                selectedId={searchType}
                segments={[
                  { id: "llm", label: "LLM" },
                  { id: "grep", label: "Grep" },
                ]}
                onSegmentChange={(segmentId) => {
                  if (isSearchType(segmentId)) {
                    handleSearchTypeChange(segmentId);
                  }
                }}
              />
            </div>
            <div className={styles.topActions}>
              <button
                ref={recentButtonRef}
                type="button"
                className={clsx(
                  styles.iconAction,
                  isRecentOpen && styles.iconActionActive
                )}
                onClick={() => setIsRecentOpen((prev) => !prev)}
                title="Recent searches"
                aria-label="Recent searches"
                aria-expanded={isRecentOpen}
              >
                <Icon name="history" />
              </button>
              <button
                type="button"
                className={styles.iconAction}
                onClick={handleNewSearch}
                title="New search"
                aria-label="New search"
              >
                <Icon name="add" />
              </button>
            </div>
          </div>
          <div className={styles.inputShell}>
            <VscodeTextarea
              ref={handleSearchTextareaRef}
              className={styles.textarea}
              placeholder={
                searchType === "grep"
                  ? "Search for text..."
                  : "Ask a question about this transcript..."
              }
              value={query}
              onInput={handleQueryInput}
              onKeyDown={handleKeyDown}
              spellCheck={false}
              autocomplete="off"
            />
          </div>
          <div className={styles.controlsRow}>
            <div className={styles.modeRow}>
              {searchType === "grep" ? (
                <div className={styles.modeControls}>
                  <ModeToggle
                    icon="matchCase"
                    title="Ignore Case"
                    active={grepOptions.ignoreCase}
                    onClick={() => toggleGrepOption("ignoreCase")}
                  />
                  <ModeToggle
                    icon="matchRegex"
                    title="Regex"
                    active={grepOptions.regex}
                    onClick={() => toggleGrepOption("regex")}
                  />
                  <ModeToggle
                    icon="matchWholeWord"
                    title="Whole Word"
                    active={grepOptions.wordBoundary}
                    onClick={() => toggleGrepOption("wordBoundary")}
                  />
                </div>
              ) : (
                <div className={styles.modelPill}>
                  <div className={styles.modelInputShell}>
                    <AutocompleteInput
                      id={modelInputId}
                      className={styles.modelInput}
                      placeholder={defaultModel ?? "e.g., openai/gpt-5.4"}
                      value={model}
                      onChange={handleModelChange}
                      onCommit={handleModelCommit}
                      suggestions={modelHistory.history}
                      allowBrowse={modelHistory.history.length > 0}
                      required
                    />
                  </div>
                </div>
              )}
            </div>
            <button
              type="submit"
              className={styles.runButton}
              disabled={loading || !query.trim()}
            >
              Run
            </button>
          </div>
        </form>
        <LoadingBar loading={loading} />
        <div className={styles.results}>
          <SearchResults
            loading={loading}
            error={error}
            hasSearched={hasSearched}
            currentSearch={currentSearch}
            scope={scope}
            searchType={searchType}
            getMessageUrl={getMessageUrl}
            getEventUrl={getEventUrl}
            getEventMessageUrl={getEventMessageUrl}
          />
        </div>
      </div>
      <PopOver
        id={`recent-searches-${api.cacheKey}`}
        isOpen={isRecentOpen}
        setIsOpen={setIsRecentOpen}
        // eslint-disable-next-line react-hooks/refs -- positionEl accepts null; PopOver/Popper handles this in effects and updates when ref is populated
        positionEl={recentButtonRef.current}
        placement="bottom-end"
        offset={[0, 2]}
        hoverDelay={-1}
        closeOnMouseLeave={false}
        showArrow={false}
        styles={{
          padding: 0,
          width: "20rem",
          maxWidth: "calc(100vw - 1rem)",
          maxHeight: "60vh",
          overflowY: "auto",
        }}
      >
        <RecentSearches
          api={api}
          searchType={searchType}
          onSelect={handleSelectRecent}
        />
      </PopOver>
    </div>
  );
};

const ModeToggle: FC<{
  active: boolean;
  icon: IconName;
  title: string;
  onClick: () => void;
}> = ({ active, icon, title, onClick }) => (
  <button
    type="button"
    className={clsx(styles.modeToggle, active && styles.modeToggleActive)}
    onClick={onClick}
    aria-pressed={active}
    title={title}
  >
    <Icon name={icon} />
  </button>
);

const RecentSearches: FC<{
  api: SearchPanelApi;
  searchType: SearchType;
  onSelect: (search: SearchInput) => void;
}> = ({ api, searchType, onSelect }) => {
  const searches = useSearches({ api, searchType });

  if (searches.isLoading) {
    return <div className={styles.recentEmpty}>Loading recent searches…</div>;
  }

  if (searches.error) {
    return (
      <div className={styles.recentEmpty}>Unable to load recent searches.</div>
    );
  }

  const items = searches.data?.items ?? [];

  if (items.length === 0) {
    return <div className={styles.recentEmpty}>No recent searches.</div>;
  }

  return (
    <ul className={styles.recentList} role="listbox">
      {items.map((search) => (
        <li
          key={search.search_id}
          role="option"
          aria-selected={false}
          className={styles.recentItem}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(search);
          }}
        >
          {search.query}
        </li>
      ))}
    </ul>
  );
};

// Phrases cycle as elapsed time crosses each threshold (seconds). LLM searches
// are bounded by upstream provider latency, so a long wait is normal — evolving
// copy reframes it as steady progress rather than a stall. Ordered high-to-low
// so the first match wins.
const SEARCHING_STAGES: { after: number; message: string }[] = [
  { after: 45, message: "Still working through the corpus" },
  { after: 31, message: "Checking the answer" },
  { after: 22, message: "Asking the model" },
  { after: 14, message: "Reading long passages" },
  { after: 9, message: "Scanning relevant sections" },
  { after: 0, message: "Searching" },
];

const SearchingIndicator: FC<{ searchType: SearchType }> = ({ searchType }) => {
  // Mounts only while loading, so the elapsed clock resets per search.
  const [start] = useState(() => performance.now());
  const [elapsed, setElapsed] = useState(0);

  useInterval(() => {
    setElapsed(Math.floor((performance.now() - start) / 1000));
  }, 1000);

  const message =
    searchType === "grep"
      ? "Searching"
      : (SEARCHING_STAGES.find((stage) => elapsed >= stage.after)?.message ??
        "Searching");

  return (
    <div className={styles.emptyState}>
      <p className={styles.searchingText}>
        <Icon name="search" /> {message}
        <span className={styles.searchingElapsed}> · {elapsed}s</span>
      </p>
    </div>
  );
};

const SearchResults: FC<{
  loading: boolean;
  error: Error | null;
  hasSearched: boolean;
  currentSearch: Result | null;
  scope: SearchScope;
  searchType: SearchType;
  getMessageUrl: (id: string) => string | undefined;
  getEventUrl: (id: string) => string | undefined;
  getEventMessageUrl: (id: string) => string | undefined;
}> = ({
  loading,
  error,
  hasSearched,
  currentSearch,
  scope,
  searchType,
  getMessageUrl,
  getEventUrl,
  getEventMessageUrl,
}) => {
  if (loading) {
    return <SearchingIndicator searchType={searchType} />;
  }
  if (error) {
    return (
      <div className={styles.emptyState}>
        <p>
          {error instanceof ApiError ? error.status : ""} Something went wrong
        </p>
        <p>{error.message}</p>
      </div>
    );
  }
  if (!hasSearched) {
    return null;
  }
  // currentSearch is null when the active search hasn't been run for this
  // transcript yet (cache miss). Render nothing — only show "No results
  // found" once we have a result that turned out to be empty.
  if (currentSearch === null) {
    return null;
  }

  const result = currentSearch;
  const numericValue = typeof result.value === "number" ? result.value : null;
  const isEmpty =
    numericValue === 0 &&
    !result.explanation &&
    typeof result.value !== "string";

  if (isEmpty) {
    return <div className={styles.emptyState}>No results found</div>;
  }

  return (
    <>
      <div className={styles.sectionHeader}>
        <span>Results</span>
        {numericValue !== null && numericValue > 0 && (
          <span className={styles.matchCount}>
            {numericValue} {numericValue === 1 ? "match" : "matches"}
          </span>
        )}
      </div>
      <SearchResult
        result={result}
        scope={scope}
        searchType={searchType}
        getMessageUrl={getMessageUrl}
        getEventUrl={getEventUrl}
        getEventMessageUrl={getEventMessageUrl}
      />
    </>
  );
};

const SearchResult: FC<{
  result: Result;
  scope: SearchScope;
  searchType: SearchType;
  getMessageUrl: (id: string) => string | undefined;
  getEventUrl: (id: string) => string | undefined;
  getEventMessageUrl: (id: string) => string | undefined;
}> = ({
  result,
  scope,
  searchType,
  getMessageUrl,
  getEventUrl,
  getEventMessageUrl,
}) => {
  const markdownRefs = useMemo((): MarkdownReference[] => {
    const seen = new Set<string>();
    const refs: MarkdownReference[] = [];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    for (const ref of result.references ?? []) {
      if (ref.cite && !seen.has(ref.cite)) {
        seen.add(ref.cite);
        const route =
          ref.type === "message"
            ? scope === "events"
              ? getEventMessageUrl(ref.id)
              : getMessageUrl(ref.id)
            : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              ref.type === "event"
              ? getEventUrl(ref.id)
              : undefined;
        refs.push({
          id: ref.id,
          cite: ref.cite,
          citeUrl: route ? `#${route}` : undefined,
        });
      }
    }
    return refs;
  }, [
    result.references,
    scope,
    getMessageUrl,
    getEventUrl,
    getEventMessageUrl,
  ]);

  if (searchType === "grep") {
    return (
      <div className={styles.resultCard}>
        {result.explanation && (
          <MarkdownDivWithReferences
            markdown={result.explanation}
            references={markdownRefs}
            renderer="textOnly"
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.resultCard}>
      {typeof result.value === "string" && (
        <MarkdownDivWithReferences
          markdown={result.value}
          references={markdownRefs}
          renderer="textOnly"
        />
      )}
      {result.explanation && (
        <>
          <div className={styles.sectionHeader}>
            <span>Explanation</span>
          </div>
          <MarkdownDivWithReferences
            markdown={result.explanation}
            references={markdownRefs}
            renderer="textOnly"
          />
        </>
      )}
    </div>
  );
};
